import React from 'react';
import {
  View,
  StyleSheet,
  Pressable,
  Platform
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Text, Theme } from '../ui';
import { useAudioPlayer } from '../../contexts/AudioPlayerContext';
import { useNavigation } from '@react-navigation/native';

const MinimalAudioPlayer: React.FC = () => {
  const navigation = useNavigation<any>();
  const {
    currentSegment,
    currentIndex,
    playlist,
    isPlaying,
    isLoading,
    position,
    duration,
    book,
    insights,
    isMinimalPlayerVisible,
    isFullPlayerVisible,
    playPause,
    pause,
    showFullPlayer,
    hideMinimalPlayer,
  } = useAudioPlayer();

  // Don't render if not visible, no current segment, or full player is visible
  if (!isMinimalPlayerVisible || !currentSegment || isFullPlayerVisible) return null;

  // Calculate progress
  const progress = duration > 0 ? position / duration : 0;
  
  // Calculate overall progress across all segments
  const totalDuration = playlist.reduce((acc, segment) => acc + segment.duration, 0);
  const segmentPositions = playlist.reduce((acc, segment, index) => {
    const previousEnd = index > 0 ? acc[index - 1].end : 0;
    acc.push({
      start: previousEnd,
      end: previousEnd + segment.duration,
    });
    return acc;
  }, [] as Array<{ start: number; end: number }>);
  
  const currentSegmentPosition = currentIndex >= 0 ? segmentPositions[currentIndex] : null;
  const overallPosition = currentSegmentPosition 
    ? currentSegmentPosition.start + position 
    : 0;
  const overallProgress = totalDuration > 0 ? overallPosition / totalDuration : 0;

  const handlePress = () => {
    showFullPlayer();
    
    // Navigate only to insights
    if (currentSegment.type === 'insight') {
      const insightIndex = insights.findIndex(i => i.id === currentSegment.id);
      navigation.navigate('InsightDetail', {
        insights,
        book,
        bookTitle: book?.title,
        bookCoverUrl: book?.coverImageUrl || book?.coverImageFullUrl,
        currentIndex: insightIndex,
      });
    }
  };

  const handleClose = async () => {
    // Pause audio and hide player (but keep playlist loaded)
    if (isPlaying) {
      await pause();
    }
    hideMinimalPlayer();
  };

  return (
    <View style={styles.container}>
      {/* Progress bar */}
      <View style={styles.progressBar}>
        <View style={[styles.progressFill, { width: `${overallProgress * 100}%` }]} />
      </View>
      
      <Pressable style={styles.content} onPress={handlePress}>
        <View style={styles.info}>
          <Text style={styles.title} numberOfLines={1}>
            {currentSegment.title}
          </Text>
          <Text style={styles.subtitle} numberOfLines={1}>
            {currentSegment.type === 'introduction' ? 'Introduction' : `Insight ${currentIndex + 1}`}
            {book?.title && ` • ${book.title}`}
          </Text>
        </View>
        
        <View style={styles.controls}>
          <Pressable 
            style={styles.playButton}
            onPress={(e) => {
              e.stopPropagation();
              playPause();
            }}
          >
            {isLoading ? (
              <Text style={styles.loadingText}>...</Text>
            ) : (
              <Ionicons 
                name={isPlaying ? "pause" : "play"} 
                size={20} 
                color={Theme.colors.textPrimary} 
              />
            )}
          </Pressable>
          
          <Pressable 
            style={styles.closeButton}
            onPress={(e) => {
              e.stopPropagation();
              handleClose();
            }}
          >
            <Ionicons 
              name="close" 
              size={16} 
              color={Theme.colors.textSecondary} 
            />
          </Pressable>
        </View>
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 70, // Position above tab bar (tab bar height is 70)
    left: 0,
    right: 0,
    backgroundColor: Theme.colors.surface,
    borderTopWidth: 1,
    borderTopColor: Theme.colors.border,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
  },
  progressBar: {
    height: 2,
    backgroundColor: Theme.colors.border,
  },
  progressFill: {
    height: '100%',
    backgroundColor: Theme.colors.primary,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Theme.spacing.lg,
    paddingVertical: Theme.spacing.md,
    gap: Theme.spacing.md,
  },
  info: {
    flex: 1,
  },
  title: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.medium,
    color: Theme.colors.textPrimary,
    marginBottom: 2,
  },
  subtitle: {
    fontSize: 12,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
  },
  controls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.sm,
  },
  playButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: Theme.colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 12,
    color: Theme.colors.textPrimary,
  },
});

export default MinimalAudioPlayer; 